/**
 * lib/raiseFlag.ts
 * ------------------------------------------------------------------
 * Shared "raise a flag" helper. Both the standalone Comment Flags
 * screen AND inline flag icons in ReviewWorkspace call this — so
 * there is ONE source of truth for the payload shape, the teacher
 * notification, and the editor-routing logic (P3 security: a flag
 * must always reach an editor for that form, or all editors if
 * unrouted).
 *
 * Extracted from src/features/comments/CommentFlags.tsx as part of
 * the inline-flag UX work. Behaviour is identical — only the
 * call-site changes. CommentFlags.tsx now imports raiseFlag() too.
 * ------------------------------------------------------------------
 */
import { addDoc, collection } from 'firebase/firestore';
import { db } from './firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { sanitizeComment } from './sanitize';
import { logAudit } from './audit';
import {
  editorsForClass,
  buildFlagNotification,
} from './notifications';
import type {
  CommentFlag,
  FlagTarget,
  SchoolClass,
  Student,
  Subject,
  Teacher,
  AppUser,
} from '@/types';

/** Reasons offered in the flag UI — one source of truth. */
export const FLAG_REASONS = [
  'Spelling / grammar',
  'Inappropriate wording',
  'Inconsistent with marks',
  'Incomplete / missing',
  'Wrong student / subject',
  'Other',
] as const;

export type FlagReason = (typeof FLAG_REASONS)[number];

/** Human label for the flag target shown in notifications + UI. */
export const FLAG_TARGET_LABELS: Record<FlagTarget, string> = {
  subjectComment: 'Subject comment',
  classTeacherComment: 'Class teacher comment',
  hodComment: 'HOY / HOD comment',
  mark: 'Mark',
};

export interface RaiseFlagInput {
  year: string;
  term: string;
  classId: string;
  studentId: string;
  target: FlagTarget;
  reason: string;
  notes: string;
  // optional, depends on target
  subjectId?: string;
  // ambient context for routing + denormalised display fields
  classes: SchoolClass[];
  students: Student[];
  subjects: Subject[];
  teachers: Teacher[];
  users: AppUser[];
  /** Who is doing the flagging (the editor). */
  flaggedById: string;
  flaggedByName: string;
}

export interface RaiseFlagResult {
  flagId: string;
  unrouted: boolean; // true when no editor owns this form
  message: string;
}

/**
 * Write a flag + teacher notification + editor notifications.
 * Throws on Firestore failure (caller catches and shows a message).
 */
export async function raiseFlag(
  input: RaiseFlagInput,
): Promise<RaiseFlagResult> {
  const {
    year,
    term,
    classId,
    studentId,
    target,
    reason,
    notes,
    subjectId,
    classes,
    students,
    subjects,
    teachers,
    users,
    flaggedById,
    flaggedByName,
  } = input;

  if (!classId || !studentId)
    throw new Error('Class and student are required.');

  const student = students.find((s) => s.id === studentId);
  const cls = classes.find((c) => c.id === classId);
  const subj = subjects.find((s) => s.id === subjectId);
  const teacherId =
    target === 'subjectComment' || target === 'mark'
      ? student?.subjectTeachers?.[subjectId ?? '']
      : cls?.classTeacherId;
  const teacher = teachers.find((t) => t.id === teacherId);

  const payload: Omit<CommentFlag, 'id'> = {
    status: 'open',
    target,
    year,
    term,
    classId,
    className: cls?.name,
    studentId,
    studentName: student?.fullName ?? '',
    subjectId: subjectId || undefined,
    subjectName: subj?.name,
    teacherId: teacherId || undefined,
    teacherName: teacher?.displayName,
    reason,
    notes: sanitizeComment(notes, 500),
    flaggedById,
    flaggedByName,
    createdAt: new Date().toISOString(),
  };
  // Strip undefined values — Firestore rejects { key: undefined }
  // (same defence as the MarkEntry save fix; same hazard).
  const cleaned = Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined),
  ) as Omit<CommentFlag, 'id'>;

  const flagRef = await addDoc(
    collection(db, COLLECTIONS.flags),
    cleaned,
  );

  // The CONCERNED TEACHER's bell + web-push + email are all created SERVER-SIDE
  // by the onCommentFlagged Cloud Function (trigger on reportFlags create),
  // addressed to the teacher's real user uid. Doing it there — not here — makes
  // it correct regardless of the editor's client build, and avoids the earlier
  // bug where the notification was addressed to the teacher-RECORD id (so it
  // never reached the teacher's bell). Do NOT recreate it on the client.

  // Route to the editor(s) responsible for this student's form.
  // If none, all editors are notified (never unrouted).
  const { recipients, unrouted } = editorsForClass(
    users,
    cls?.name ?? '',
  );
  for (const ed of recipients) {
    await addDoc(collection(db, COLLECTIONS.notifications), {
      ...buildFlagNotification(
        {
          studentName: student?.fullName ?? 'Student',
          className: cls?.name,
          reasonLabel: reason,
          reason,
          target,
          subjectName: subj?.name,
          classId,
          studentId,
        },
        ed.uid,
        flagRef.id,
      ),
    });
  }

  // Round 10: audit the flag-raise event so the per-student history
  // and the global Activity Log both pick it up.
  logAudit({
    action: 'flag_raised',
    category: 'academic',
    actorId: flaggedById,
    actorName: flaggedByName,
    targetId: flagRef.id,
    targetType: 'comment',
    targetName: `Flag on ${student?.fullName ?? 'student'}`,
    details: {
      flagId: flagRef.id,
      target,
      reason,
      notes: cleaned.notes ?? '',
      year,
      term,
      classId,
      className: cls?.name,
      studentId,
      studentName: student?.fullName,
      subjectId: subjectId || undefined,
      subjectName: subj?.name,
      teacherId: teacherId || undefined,
      teacherName: teacher?.displayName,
      unrouted,
    },
  });

  return {
    flagId: flagRef.id,
    unrouted,
    message: unrouted
      ? 'Flag raised. No editor is assigned to this form — all editors notified.'
      : 'Flag raised; teacher and the form editor notified.',
  };
}
